<script setup lang="ts">
import { i18n } from '@/i18n';
import { OrderGroupModalState } from '@/orderTableModule/constants';
import { useOrderTableStore } from '@/orderTableModule/pages/useOrderTableStore';
import { storeToRefs } from 'pinia';
import { computed, h, watch } from 'vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { DateService } from '@/coreDomainsModule/services/dateService';
import SvgLoader from '@/utils/SvgLoader.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import OrderGroupSelectModal from './OrderGroupSelectModal.vue';

const t = i18n.t;
const store = useOrderTableStore();
const { state } = storeToRefs(store);
const { postOrderToOrderGroupLinking, resetAndFetch, showOrderAmountOverOrderGroupLimit } = store;

const isOpening = computed(() => state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupAdd);

const targetOrderGroupData = computed(() =>
  state.value.orderGroupState.groupList.find((group) => group.orderGroupName === state.value.orderGroupState.targetGroupName)
);

const selectedOrderData = computed(() => state.value.items.filter((item) => state.value.checkedIds.includes(item.orderId)));

const selectedOrderDataTotalAmount = computed(() => selectedOrderData.value.reduce((acc, order) => (acc += order.orderAmount), 0));

watch(isOpening, (val) => {
  if (!val) {
    state.value.orderGroupState.targetGroupName = '';
  }
});
/**
 * nextボタンを押した際の動作
 */
const onNext = async () => {
  // バリデーション
  if (!targetOrderGroupData.value) {
    // グループ情報がStateにない
    console.error('グループがStateに存在しない');
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('エラーが発生しました。リロードして再度お試しください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  } else if (
    targetOrderGroupData.value.totalLinkedOrderAmount + selectedOrderDataTotalAmount.value >
    targetOrderGroupData.value.upperLimitQuantity
  ) {
    // 上限数量を超過
    await showOrderAmountOverOrderGroupLimit(
      targetOrderGroupData.value,
      selectedOrderData.value.length,
      selectedOrderDataTotalAmount.value
    );
    return;
  } else if (!selectedOrderData.value.every((data) => data.orderGroupId === null || data.orderGroupId === undefined)) {
    // 他のグループに既に追加されている
    await showInfoModal({
      modalTitle: t('既にグループに追加されている発注が選択されています'),
      modalDescription: t('別の発注グループに追加したい場合は、一度発注グループから外してください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  } else if (selectedOrderData.value.some((data) => data.partialDeliveries.length > 0)) {
    // 追加しようとした取引に分納がある
    await showInfoModal({
      modalTitle: t('分納がある取引が選択されています'),
      modalDescription: t('分納がある取引を発注グループに追加することはできません。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }
  // 発注の納期が発注グループとあっていない場合は納期を変更するかどうかの確認モーダルを出す
  let updateETA: number | undefined = !targetOrderGroupData.value.eta
    ? selectedOrderData.value[0]?.estimatedTimeOfArrival
    : targetOrderGroupData.value.eta;
  if (updateETA !== undefined && selectedOrderData.value.some((data) => data.estimatedTimeOfArrival !== updateETA)) {
    const isConfirmedChangeETA = await showInfoModal({
      modalTitle: t('納期を同じにする必要があります'),
      modalDescription: h('div', { style: { whiteSpace: 'pre' } }, [
        t('追加する発注の納期は発注グループの納期と同じ日にする必要があります。'),
        '\n',
        t('納期を発注グループと同じ日にしますか？'),
      ]),
      modalContent: h(
        'div',
        {
          style: {
            backgroundColor: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            color: '#212121',
            fontSize: '16px',
            lineHeight: '1',
          },
        },
        [
          h('div', {}, [t('変更後納期')]),
          h(
            'div',
            {
              style: {
                width: '100%',
                padding: '14px 0 6px 0',
                display: 'grid',
                gap: '10px',
                gridTemplateColumns: 'auto auto',
                justifyContent: 'center',
                alignItems: 'center',
              },
            },
            [
              h(SvgLoader, { name: 'calendar_today_gray2x' }),
              DateService.convertIYearMonthDayToYYYYMMDDWithSlash(DateService.convertUnixTimeToIDate(updateETA / 1000)),
            ]
          ),
        ]
      ),
      backButtonLabel: t('キャンセル'),
      nextButtonLabel: t('納期変更して追加'),
      modalWidth: 764,
    });

    if (!isConfirmedChangeETA) return;
  } else {
    updateETA = undefined;
  }
  const linkingOrderToOrderGroupResult = await waitLoading(
    postOrderToOrderGroupLinking(updateETA),
    t('発注を発注グループに追加中です...'),
    ''
  );
  if (!linkingOrderToOrderGroupResult) {
    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('発注グループへの追加中にエラーが発生しました。時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }
  resetAndFetch();
  await showInfoModal({
    modalTitle: t('発注グループに発注を追加しました'),
    backButtonLabel: 'OK',
    enableOutsideClick: false,
    isDisabledNextButton: true,
  });
  return;
};
</script>
<template>
  <OrderGroupSelectModal
    :is-opening="isOpening"
    :title="t('発注グループへ追加')"
    :description="t('選択した発注を発注グループに追加できます。')"
    :next-button-label="t('追加')"
    @next="onNext"
    @back="() => (state.orderGroupState.openingModal = OrderGroupModalState.None)"
  />
</template>
<style lang="scss" scoped></style>
